import React from 'react';
import { InjectedFormProps } from 'redux-form';
import { Field, FieldArray } from 'redux-form/immutable';

import FormTextField from 'retired/FormTextField';
import FormDescriptionRteField from 'retired/FormDescriptionRteField';
import * as validators from 'utils/validators';
import FormNonprofitsSelector from 'retired/FormNonprofitsSelector';
import FormContentSelector from 'retired/FormContentSelector';
import { MinAction } from 'shared/types';

import { StyleSection } from './StyleSection';
import { AddActionsSection } from './AddActionsSection';
import { GeneralSettingsSection } from './GeneralSettingsSection';
import type { FormData } from './types';

const notEmpty = validators.notEmpty();
const maxLength50 = validators.maxLength(50);
const maxLength100 = validators.maxLength(100);
const maxLength5000 = validators.maxLengthRichText(5000);
const isURL = validators.isURL();
const isSupportedVideoLink = validators.isSupportedVideoLink();

/**
 * @NOTE-BI
 *
 * Exclude these steps when a giving pledge campaign is being created/viewed
 */
const PLEDGE_STEPS_EXCLUSION_ID_LIST = ['fundraisers', 'volunteering', 'actions', 'events'];

interface SingleFieldInterface {
  name: string;
  label: string;
  helperText: string;
  validate?: ((value: unknown) => string | undefined)[];
  multiline?: boolean;
  isRTE?: boolean;
}

const getHeaderStepContent = (): JSX.Element => {
  const fields: SingleFieldInterface[] = [
    {
      name: 'title',
      label: 'Campaign Title *',
      helperText: 'Max. 100 characters',
      validate: [notEmpty, maxLength100],
    },
    {
      name: 'description',
      label: 'Tagline *',
      helperText: 'Max. 50 characters',
      validate: [notEmpty, maxLength50],
    },
    {
      name: 'cta',
      label: 'Call to Action *',
      helperText: 'Max. 50 characters',
      validate: [notEmpty, maxLength50],
    },
  ];
  return (
    <div id="step-header">
      {fields.map((field: SingleFieldInterface) => (
        <Field
          key={field.name}
          style={{ marginBottom: '20px' }}
          component={FormTextField}
          fullWidth
          name={field.name}
          label={field.label}
          helperText={field.helperText}
          validate={field.validate}
        />
      ))}
    </div>
  );
};

const getDescriptionSectionStep = (): JSX.Element => {
  const fields: SingleFieldInterface[] = [
    {
      name: 'videoLink',
      label: 'Video Link',
      helperText: 'Enter a youtube, vimeo or brightcove embed link',
      validate: [isURL, isSupportedVideoLink],
    },
    {
      name: 'totalHeadline',
      label: 'Title *',
      helperText: 'Max. 100 characters',
      validate: [notEmpty, maxLength100],
    },
    {
      name: 'totalText',
      label: 'Description *',
      helperText: 'Max. 5000 characters',
      validate: [notEmpty, maxLength5000],
      multiline: true,
      isRTE: true,
    },
  ];
  return (
    <div id="step-description">
      {fields.map((field: SingleFieldInterface, index: number) => (
        <Field
          key={field.name}
          style={index < fields.length - 1 ? { marginBottom: '20px' } : undefined}
          component={field.isRTE ? FormDescriptionRteField : FormTextField}
          fullWidth
          name={field.name}
          label={field.label}
          helperText={field.helperText}
          validate={field.validate}
          multiline={field.multiline}
        />
      ))}
    </div>
  );
};

const getAttachNonprofitsStepContent = (isPledge?: boolean): JSX.Element => (
  <div id="step-nonprofits">
    <FieldArray
      name="nonprofits"
      component={FormNonprofitsSelector}
      acceptsDonations
      showAutoTagDonations={!isPledge}
      showLinkToDonateSearch
    />
  </div>
);

const getAttachDeedsStepContent = (contentType: string, deedTypes: string[]): JSX.Element => (
  <div id={`step-${contentType}`}>
    <FieldArray
      name={contentType}
      component={FormContentSelector}
      contentType={contentType}
      deedTypes={deedTypes}
    />
  </div>
);

export const getSteps = ({
  props,
  image,
  setImage,
  showActionsStep,
  campaignId,
  selectedActions,
  setSelectedActions,
  isPledge,
  actionsSorting,
}: {
  props: InjectedFormProps<FormData>;
  image: boolean;
  isPledge?: boolean;
  setImage: (image: boolean) => void;
  showActionsStep: boolean;
  campaignId?: string;
  selectedActions: MinAction[];
  setSelectedActions: (actions: MinAction[]) => void;
  actionsSorting: string[];
}): { label: string; id: string; jsx: JSX.Element }[] => {
  const allSteps = [
    {
      label: 'Header and Banner Settings *',
      id: 'header',
      jsx: getHeaderStepContent(),
    },
    {
      label: 'Description *',
      id: 'description',
      jsx: getDescriptionSectionStep(),
    },
    {
      label: 'Styling *',
      id: 'styling',
      jsx: (
        <div id="step-styling">
          <StyleSection image={image} setImage={setImage} />
        </div>
      ),
    },
    {
      label: 'Select Nonprofits to Donate to',
      id: 'nonprofits',
      jsx: getAttachNonprofitsStepContent(isPledge),
    },
    {
      label: 'Select and Order Fundraisers',
      id: 'fundraisers',
      jsx: getAttachDeedsStepContent('fundraisers', ['Campaign']),
    },
    {
      label: 'Select and Order Volunteering',
      id: 'volunteering',
      jsx: getAttachDeedsStepContent('volunteering', ['Project', 'Event']),
    },
    {
      label: 'Select and Order Events',
      id: 'events',
      jsx: getAttachDeedsStepContent('events', ['BaseEvent']),
    },
    ...(showActionsStep
      ? [
          {
            label: 'Select Actions',
            id: 'actions',
            jsx: (
              <div id="step-actions">
                <AddActionsSection
                  campaignId={campaignId}
                  selectedActions={selectedActions}
                  setSelectedActions={setSelectedActions}
                  actionsSorting={actionsSorting}
                />
              </div>
            ),
          },
        ]
      : []),
    {
      label: 'General Settings *',
      id: 'general',
      jsx: (
        <div id="step-general">
          <GeneralSettingsSection parentProps={props} isPledge={isPledge} />
        </div>
      ),
    },
  ];

  /**
   * @NOTE-BI
   * A Giving Pledge Campaign should not have the following steps
   **/
  if (isPledge) {
    return allSteps.filter((step) => !PLEDGE_STEPS_EXCLUSION_ID_LIST.includes(step.id));
  }

  return allSteps;
};
